import { useState, useCallback, useRef, useEffect } from 'react';
import { createChatClient, getWidgetConfig, resolveBaseUrl } from '@chanl/widget-sdk';
import type { ChatSession, ChanlUser, ClientContext } from '@chanl/widget-sdk';
import type { AgentConfig, ChatMessage } from './types';

// ── postMessage bridge to widget loader ──
//
// The chat UI runs inside an iframe on chat-host origin. The widget loader
// runs on the host page (customer's origin) and owns localStorage. Token
// lifecycle events bounce through window.parent.postMessage with the
// existing `chanl:` namespace the loader already listens for. Guard the
// post so it's a no-op when the iframe runs standalone (agent test panel,
// direct-URL navigation) — otherwise we'd leak token events to whatever
// context happens to be embedding us.

function postToParent(type: string, payload?: unknown): void {
  if (typeof window === 'undefined' || window.parent === window) return;
  try {
    window.parent.postMessage({ type: `chanl:${type}`, payload }, '*');
  } catch {
    // postMessage can throw if the parent is closed; ignore.
  }
}

/**
 * Read `?sessionToken=...` from the current iframe URL. The loader sets
 * this when it found a stored token on the host page; chat-ui uses it to
 * call /resume instead of /chat on first mount.
 */
function readUrlSessionToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return new URL(window.location.href).searchParams.get('sessionToken');
  } catch {
    return null;
  }
}

// ── Types ──

interface UseChanl {
  messages: ChatMessage[];
  isTyping: boolean;
  error: string | null;
  send: (text: string) => void;
  sessionReady: boolean;
  agentConfig: AgentConfig | null;
  /** Set when a human took the thread over — the header swaps to the rep's identity. */
  activeHandoff: import('@chanl/widget-sdk').StreamHandoff | null;
  /**
   * Wipe the conversation back to the greeting + create a fresh session on
   * next send. Best-effort ends the current Interaction server-side. Used by
   * the widget header's "New conversation" button.
   */
  resetSession: () => void;
}

interface UseChanlOptions {
  agentId: string;
  apiKey: string;
  baseUrl?: string;  // Auto-detects localhost vs production
  greeting?: string;
  // Verified end-user identity (from the iframe URL params the loader set).
  // Threaded into createChatClient so createSession ties the interaction to a
  // Customer record; the backend verifies userHash. Anonymous when absent.
  user?: ChanlUser;
  userHash?: string;
  // Stable anonymous visitor id (from the `anonymousId` URL param the loader set,
  // or minted by this iframe as a fallback). Threaded into createChatClient so a
  // cold visitor maps to ONE `anon_<id>` Customer across sessions instead of a
  // fresh one each session. `user.externalId` takes precedence over this.
  anonymousId?: string;
  // HOST-page page-level context (pageUrl/referrer/utm) forwarded by the embed
  // loader as `ctx*` params. Passed to createSession's `input.clientContext`
  // so these host-page values override the iframe-local page-level fields the
  // chat-client collects (caller wins per-field). Browser-level fields
  // (userAgent/language/timezone/screen) are collected by the chat-client and
  // are correct from inside the iframe — not forwarded here.
  clientContext?: ClientContext;
}

// ── Helpers ──

let counter = 0;
function id() { return `sdk-${++counter}-${Date.now()}`; }

// Shown when a human has taken over the thread (handlerMode='human') and the
// AI reply is suppressed. Replaces the raw suppression frames that used to leak
// into the bubble; mirrors Intercom's "a teammate will reply" affordance.
const HANDOFF_TEXT = 'A team member will reply to you here shortly.';
const GENERIC_ERROR_TEXT = 'Something went wrong. Please try again.';

function toolLabel(toolName: string, state: 'call' | 'result'): string {
  // Friendly labels — in production these come from agent config
  const labels: Record<string, { call: string; result: string }> = {
    search_listings: { call: 'Searching listings...', result: '✓ Found results' },
    lookup_order: { call: 'Looking up order...', result: '✓ Order found' },
    get_available_slots: { call: 'Checking availability...', result: '✓ Slots found' },
    check_order_status: { call: 'Checking order status...', result: '✓ Status retrieved' },
    calendar_booking: { call: 'Booking appointment...', result: '✓ Booked' },
    send_email: { call: 'Sending email...', result: '✓ Email sent' },
    knowledge_search: { call: 'Searching knowledge base...', result: '✓ Found answers' },
  };
  const entry = labels[toolName] ?? { call: 'Working on it...', result: '✓ Done' };
  return state === 'call' ? entry.call : entry.result;
}

// ── Hook ──

export function useChanl({ agentId, apiKey, baseUrl, greeting, user, userHash, anonymousId, clientContext }: UseChanlOptions): UseChanl {
  const resolvedBaseUrl = resolveBaseUrl(baseUrl);
  const clientRef = useRef(createChatClient({ apiKey, baseUrl: resolvedBaseUrl, user, userHash, anonymousId }));
  const sessionRef = useRef<ChatSession | null>(null);
  const isStreamingRef = useRef(false);

  // Identity received via a loader `set-user` AFTER mount (Intercom-style:
  // boot anonymous, identify later once the host knows the user). Applied to a
  // live session immediately via identify(), and stashed so any session created
  // afterwards is identified from creation (ensureSession reads this ref).
  const pendingIdentityRef = useRef<{ user: ChanlUser; userHash?: string } | null>(null);

  // Hold the host-page context in a ref so the memoized `ensureSession` always
  // reads the latest value without re-creating the callback (mirrors how the
  // client itself is held). The context arrives from URL params at mount and
  // doesn't change during the iframe's lifetime, but a ref keeps the
  // create-on-first-send path robust if it ever does.
  const clientContextRef = useRef(clientContext);
  clientContextRef.current = clientContext;

  // Context handed to us AFTER mount by `ChanlChat.setContext(...)`. Kept
  // separate from `clientContextRef` (which mirrors the mount-time `ctx*` URL
  // params on every render) so a host call isn't clobbered by the next render.
  // Both session-creation paths layer this over the params, host wins per-field.
  const hostContextRef = useRef<ClientContext | null>(null);

  // Latest `send` for the host bridge, so its listener can subscribe once
  // instead of re-binding every time the callback identity changes.
  const sendRef = useRef<(text: string) => void>(() => {});

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [agentConfig, setAgentConfig] = useState<AgentConfig | null>(null);

  // ── Re-engagement (idle nudge) ──
  //
  // Chat is request/response — the server can't push into this iframe — so
  // the SESSION's `reengagement` policy (returned by createSession when the
  // agent enables it) drives a client-side idle timer that asks the server
  // for a nudge. The server is authoritative: it re-checks staleness + the
  // nudge budget on every call, so a mistimed request just returns `skip`.
  // Armed only after a COMPLETED agent turn; cleared on any user activity.
  // WHO owns the thread after a takeover — drives the header identity swap and
  // the parent `chanl:handoff` event. Payload rides the turn RESPONSE
  // (`response.handoff`); the stream part is only the render marker.
  const [activeHandoff, setActiveHandoff] = useState<import('@chanl/widget-sdk').StreamHandoff | null>(null);
  // The pill names the rep once the response identifies one.
  const handoffTextRef = useRef(HANDOFF_TEXT);


  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearNudgeTimer = () => {
    if (nudgeTimerRef.current) {
      clearTimeout(nudgeTimerRef.current);
      nudgeTimerRef.current = null;
    }
  };

  // Self-rescheduling closure held in a ref so stable callbacks (send,
  // resetSession) always invoke the latest instance without dep churn.
  const scheduleNudgeRef = useRef<(delaySeconds: number) => void>(() => {});
  scheduleNudgeRef.current = (delaySeconds: number) => {
    clearNudgeTimer();
    if (!sessionRef.current?.reengagement) return;
    nudgeTimerRef.current = setTimeout(async () => {
      nudgeTimerRef.current = null;
      const s = sessionRef.current;
      if (!s?.reengagement || isStreamingRef.current) return;
      try {
        const res = await clientRef.current.nudge(s.sessionId);
        if (res.directive === 'nudged' && res.message) {
          setMessages((p) => [
            ...p,
            { id: id(), role: 'assistant', content: res.message!, timestamp: Date.now() },
          ]);
          // Next window: after `endAfterSeconds` of continued silence the
          // server ends the session — ask again then (+1s for clock skew).
          scheduleNudgeRef.current(s.reengagement.endAfterSeconds + 1);
        } else if (res.directive === 'skip') {
          // Transient skips (client/server clock skew) → retry shortly.
          // Terminal skips (disabled / human_handled / session_not_active /
          // visitor_awaiting_reply / no_messages) → stop; nothing to wait for.
          if (res.reason === 'not_stale_yet' || res.reason === 'nudge_too_recent') {
            scheduleNudgeRef.current(10);
          }
        } else if (res.directive === 'end') {
          // Server ended the session (silence budget spent, or the
          // conversation had already concluded). Keep the transcript on
          // screen; drop the local session so the next send() starts fresh.
          sessionRef.current = null;
          setSessionReady(false);
          postToParent('session-token-cleared');
        }
      } catch {
        // Network/authorization failure — a proactive ping is never worth
        // surfacing an error for. Stop quietly.
      }
    }, Math.max(1, delaySeconds) * 1000);
  };

  // Fetch widget config on mount → get agent name, avatar, greeting
  useEffect(() => {
    let cancelled = false;
    getWidgetConfig(agentId, apiKey, resolvedBaseUrl).then((config) => {
      if (cancelled || !config) return;
      const agent: AgentConfig = {
        id: agentId,
        name: config.agent?.name ?? 'AI Assistant',
        initials: (config.agent?.name ?? 'AI').split(' ').map((w) => w[0]).join('').slice(0, 2),
        avatarUrl: config.style?.agentAvatar,
        greeting: greeting ?? config.banner?.description ?? `Hi! I'm ${config.agent?.name ?? 'here to help'}. How can I assist you?`,
        suggestions: [],
        primaryColor: config.style?.primaryColor,
      };
      setAgentConfig(agent);
      // Only seed the greeting when the transcript is still empty. Resume /
      // identity-continuation hydrate can win the race against getWidgetConfig;
      // never clobber an already-hydrated history with the greeting.
      setMessages((prev) =>
        prev.length > 0
          ? prev
          : [{ id: id(), role: 'assistant', content: agent.greeting, timestamp: Date.now() }],
      );
    });
    return () => { cancelled = true; };
  }, [agentId, apiKey, baseUrl, greeting]);

  // Attempt to resume an existing session from a token stashed in the
  // iframe URL by the loader. For identified visitors (externalId+userHash),
  // fall through to eager createSession on miss so int-096 identity
  // continuation hydrates history without waiting for first send.
  useEffect(() => {
    let cancelled = false;

    function hydrateFromMessages(
      history: Array<{ role: string; content: string; timestamp?: string }>,
    ) {
      if (!history.length) return;
      setMessages(
        history.map((m) => ({
          id: id(),
          role: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
          timestamp: m.timestamp ? new Date(m.timestamp).getTime() : Date.now(),
        })),
      );
    }

    async function applyCreatedSession(session: ChatSession) {
      if (cancelled) return;
      sessionRef.current = session;
      if (Array.isArray(session.messages) && session.messages.length > 0) {
        hydrateFromMessages(session.messages);
      }
      setSessionReady(true);
      if (session.sessionToken) {
        postToParent('session-token-issued', { token: session.sessionToken });
      }
      // Arm the idle timer on a RESUMED conversation too — a visitor who reloads
      // mid-conversation and then goes quiet deserves the same nudge as one who
      // never reloaded. The server re-validates every request, so arming on a
      // fresh (message-less) session costs one `skip: no_messages` at most.
      if (session.reengagement) {
        scheduleNudgeRef.current(session.reengagement.nudgeAfterSeconds + 1);
      }
    }

    async function eagerIdentifiedCreate() {
      const pending = pendingIdentityRef.current;
      const identUser = pending?.user ?? user;
      const identHash = pending?.userHash ?? userHash;
      if (!identUser?.externalId || !identHash) return;
      const clientContext = hostContextRef.current
        ? { ...(clientContextRef.current ?? {}), ...hostContextRef.current }
        : clientContextRef.current;
      const session = await clientRef.current.createSession(agentId, {
        user: identUser,
        userHash: identHash,
        ...(clientContext ? { clientContext } : {}),
      });
      await applyCreatedSession(session);
    }

    (async () => {
      const token = readUrlSessionToken();
      if (token) {
        // BOOT with the stored token — not `resumeSession`. Boot IS resume: it returns the
        // conversation the token names, its tail, and the continuation flags in one call, and
        // the id it returns is the same id a first load gets. `/resume` is a second endpoint
        // answering the same question, and the two answers disagreeing is exactly what #169
        // was — the widget routed its next message on resume's id and the gateway refused it.
        // Re-booting is cheap here because this runs in an iframe, where reloads are routine.
        //
        // No `decodeJwtPayload` / `interactionId` pre-read is needed any more: boot resolves
        // the conversation from the token server-side, so the client no longer has to parse a
        // credential to work out what it is holding.
        try {
          const clientContext = clientContextRef.current;
          const session = await clientRef.current.createSession(agentId, {
            sessionToken: token,
            ...(clientContext ? { clientContext } : {}),
          });
          if (cancelled) return;
          await applyCreatedSession(session);
          return;
        } catch {
          if (!cancelled) postToParent('session-token-cleared');
          // Fall through to identity / anon create when possible.
        }
      }

      // Identified boot with no usable token → eager create (reattach/continue).
      const pending = pendingIdentityRef.current;
      const identUser = pending?.user ?? user;
      const identHash = pending?.userHash ?? userHash;
      if (identUser?.externalId && identHash) {
        try {
          await eagerIdentifiedCreate();
        } catch {
          // Leave create-on-first-send as fallback.
        }
        return;
      }

      // Anonymous boot (no verified identity) does NOT eager-create — a visitor
      // who only opens the widget must create no Customer / interaction. This
      // reverts int-098's eager-anon boot create, whose fresh-per-boot
      // anonymousId sprayed a new anon_<uuid> Customer + empty conversation on
      // every mount (2026-07-24 bleed-stop). Anon visitors who actually send are
      // created on first message (create-on-first-send), which threads the same
      // anonymousId through the client. Verified identity above is unaffected;
      // proper anon continuation returns in ConvoCore boot-IS-resume (read-only).
    })();

    return () => {
      cancelled = true;
    };
  }, [agentId, user, userHash, anonymousId]);

  // ── Async identity (Intercom-style identify-after-boot) ──
  // The loader forwards `ChanlChat.setUser(u)` to this iframe as a
  // `chanl:set-user` postMessage (loader posts `{ type:'chanl:set-user',
  // payload: { externalId, email, name, userHash } }`). We:
  //   • upgrade a live anonymous session in place via client.identify(), and
  //   • stash the identity so a session created LATER is identified at creation
  //     (ensureSession reads pendingIdentityRef).
  // This is what makes the widget Intercom-compatible: boot anonymous, then
  // `setUser` once the host knows who the visitor is, without a reload.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onSetUser(e: MessageEvent) {
      // Only accept identity from the embedding parent (where the loader runs).
      // Without this, any script that can postMessage to this iframe could spoof
      // a `set-user` and bind the session to an arbitrary externalId. Origins
      // aren't pinned (the widget embeds on arbitrary customer origins — same
      // reason the iframe→parent posts use '*'), so source-equality is the gate;
      // a valid userHash is still required for verification-required workspaces.
      if (e.source !== window.parent) return;
      if (!e.data || e.data.type !== 'chanl:set-user') return;
      const payload = e.data.payload as Record<string, unknown> | undefined;
      // Need a stable external id to bind the Customer; ignore malformed calls.
      if (!payload || typeof payload.externalId !== 'string') return;
      const { userHash: rawHash, ...userFields } = payload;
      const nextUser = userFields as unknown as ChanlUser;
      const nextHash = typeof rawHash === 'string' ? rawHash : undefined;
      pendingIdentityRef.current = { user: nextUser, userHash: nextHash };

      const session = sessionRef.current;
      if (session) {
        // Upgrade the already-running session. Best-effort: a
        // verification-required workspace 403s without a valid userHash and the
        // session simply stays anonymous.
        clientRef.current
          .identify(session.sessionId, { user: nextUser, userHash: nextHash })
          .then((result) => {
            // The server re-issues a sessionToken bound to the now-identified
            // customer ("Widget MUST replace its stored token"). Without this,
            // the loader keeps serving the OLD anonymous token, and the next
            // page load's GET /:id/resume 403s for this visitor. Same bridge
            // message a fresh session posts (applyCreatedSession/ensureSession).
            if (result?.sessionToken && sessionRef.current) {
              sessionRef.current = { ...sessionRef.current, sessionToken: result.sessionToken };
              postToParent('session-token-issued', { token: result.sessionToken });
            }
          })
          .catch(() => {});
      }
    }
    window.addEventListener('message', onSetUser);
    return () => window.removeEventListener('message', onSetUser);
  }, []);

  // Create session lazily (on first send)
  const ensureSession = useCallback(async () => {
    if (sessionRef.current) return sessionRef.current;
    // Pass the host-page page-level context (forwarded by the embed loader and
    // decoded from `ctx*` URL params) as `input.clientContext`. The chat-client
    // merges this over the iframe-local page-level fields it collects, with the
    // caller (host) winning per-field — so pageUrl/referrer/utm reflect the
    // customer's page, not the widget iframe's URL.
    const clientContext = hostContextRef.current
      ? { ...(clientContextRef.current ?? {}), ...hostContextRef.current }
      : clientContextRef.current;
    // If a loader `set-user` arrived before any session existed, identify this
    // first session at creation (per-call user/userHash override config-level).
    const pending = pendingIdentityRef.current;
    const session = await clientRef.current.createSession(
      agentId,
      clientContext || pending
        ? {
            ...(clientContext ? { clientContext } : {}),
            ...(pending ? { user: pending.user, userHash: pending.userHash } : {}),
          }
        : undefined,
    );
    sessionRef.current = session;
    setSessionReady(true);
    // Tell loader to persist the fresh token so the next refresh resumes.
    if (session.sessionToken) {
      postToParent('session-token-issued', { token: session.sessionToken });
    }
    return session;
  }, [agentId]);

  // Send message with streaming
  const send = useCallback(async (text: string) => {
    if (isStreamingRef.current) return;
    setError(null);
    // The visitor is active — cancel any pending idle nudge.
    clearNudgeTimer();

    // Add user message
    setMessages((p) => [...p, { id: id(), role: 'user', content: text, timestamp: Date.now() }]);
    setIsTyping(true);

    try {
      const session = await ensureSession();
      // Lazy placeholder: don't create the assistant bubble until the first
      // chunk arrives. While we wait, the typing-dots indicator carries the
      // "agent is thinking" affordance alone — otherwise we'd render BOTH the
      // typing dots AND an empty assistant bubble for the gap between
      // stream-connected and first-chunk, which looks like two replies.
      let placeholderId: string | null = null;
      const startTime = Date.now();

      isStreamingRef.current = true;

      // Track status pill IDs for cleanup
      const statusIds: string[] = [];

      const response = await clientRef.current.streamMessage(
        session.sessionId,
        text,
        // onChunk — text delta
        (chunk) => {
          // Stream parser can emit empty deltas (e.g. control packets sent
          // before any actual tokens). Don't create the bubble until we have
          // real content — otherwise we get the ghost-bubble bug back: an
          // empty assistant bubble alongside the still-spinning typing dots.
          if (!placeholderId) {
            if (!chunk) return;
            const newId = id();
            placeholderId = newId;
            setIsTyping(false); // Bubble takes over for the typing dots
            setMessages((p) => [
              ...p,
              { id: newId, role: 'assistant', content: chunk, timestamp: Date.now() },
            ]);
            return;
          }
          setMessages((p) => p.map((m) =>
            m.id === placeholderId ? { ...m, content: m.content + chunk } : m,
          ));
        },
        // onPart — tool invocations, human handoff, in-band errors
        (part) => {
          // Human took over the thread → AI suppressed. Show a single handoff
          // note pinned to the bottom instead of an empty/JSON bubble.
          if (part.type === 'handoff') {
            setIsTyping(false);
            setMessages((p) => {
              const base = placeholderId ? p.filter((m) => !(m.id === placeholderId && !m.content)) : p;
              const withoutHandoff = base.filter((m) => m.role !== 'handoff');
              return [...withoutHandoff, { id: id(), role: 'handoff', content: handoffTextRef.current, timestamp: Date.now() }];
            });
            return;
          }
          // In-band stream error (backend `3:`). Friendly message, never raw.
          if (part.type === 'error') {
            setIsTyping(false);
            setMessages((p) => {
              const base = placeholderId ? p.filter((m) => !(m.id === placeholderId && !m.content)) : p;
              return [...base, { id: id(), role: 'error', content: part.error || GENERIC_ERROR_TEXT, timestamp: Date.now() }];
            });
            return;
          }
          if (part.type !== 'tool-invocation' || !part.toolInvocation) return;
          const inv = part.toolInvocation;
          const label = toolLabel(inv.toolName, inv.state);

          if (inv.state === 'call') {
            const sid = id();
            statusIds.push(sid);
            setMessages((p) => {
              // Insert status pill before placeholder
              const idx = p.findIndex((m) => m.id === placeholderId);
              const pill: ChatMessage = { id: sid, role: 'status', content: label, label, timestamp: Date.now() };
              if (idx >= 0) {
                const next = [...p];
                next.splice(idx, 0, pill);
                return next;
              }
              return [...p, pill];
            });
          } else if (inv.state === 'result') {
            // Update the most recent status pill
            const lastSid = statusIds[statusIds.length - 1];
            if (lastSid) {
              setMessages((p) => p.map((m) =>
                m.id === lastSid ? { ...m, content: label, label } : m,
              ));
            }
          }
        },
      );

      isStreamingRef.current = false;

      // Remove status pills, add thought badge + final response
      const elapsed = (Date.now() - startTime) / 1000;
      const toolCount = statusIds.length;

      // Non-streaming fallback: if no chunks ever streamed but response.message
      // arrived, materialise the bubble now from the full message.
      if (!placeholderId && response.message) {
        const newId = id();
        placeholderId = newId;
        setIsTyping(false);
      }

      const finalPlaceholderId = placeholderId;

      setMessages((p) => {
        let next = p.filter((m) => !statusIds.includes(m.id));
        if (finalPlaceholderId) {
          const exists = next.some((m) => m.id === finalPlaceholderId);
          if (exists) {
            next = next.map((m) =>
              m.id === finalPlaceholderId ? { ...m, content: response.message } : m,
            );
          } else {
            next = [
              ...next,
              { id: finalPlaceholderId, role: 'assistant', content: response.message, timestamp: Date.now() },
            ];
          }
        }
        if (toolCount > 0 && finalPlaceholderId) {
          const badgeIdx = next.findIndex((m) => m.id === finalPlaceholderId);
          if (badgeIdx >= 0) {
            next.splice(badgeIdx, 0, {
              id: id(),
              role: 'tool',
              content: `Thought for ${elapsed.toFixed(1)}s · Used ${toolCount} tool${toolCount > 1 ? 's' : ''}`,
              timestamp: Date.now(),
              duration: elapsed,
            });
          }
        }
        // The response names WHO owns the thread; the stream part was only the
        // render marker. Update the pill (it may already exist with the generic
        // text from onPart), the header identity, and the host page.
        if (response.handoff) {
          handoffTextRef.current = response.handoff.repName
            ? `${response.handoff.repName} will reply to you here.`
            : HANDOFF_TEXT;
          next = next.map((m) =>
            m.role === 'handoff' ? { ...m, content: handoffTextRef.current } : m,
          );
          setActiveHandoff(response.handoff);
          postToParent('handoff', response.handoff);
        }
        // Defensive: if the turn was suppressed (human handoff) or errored in
        // band but the streaming onPart didn't already render it, surface it now
        // so the user never sees an empty bubble (or, pre-fix, raw JSON).
        if (response.suppressed && !next.some((m) => m.role === 'handoff')) {
          next = next.filter((m) => m.role !== 'handoff');
          next.push({ id: id(), role: 'handoff', content: handoffTextRef.current, timestamp: Date.now() });
        }
        if (response.error && !next.some((m) => m.role === 'error')) {
          next.push({ id: id(), role: 'error', content: response.error, timestamp: Date.now() });
        }
        return next;
      });
      if (response.suppressed || response.error) setIsTyping(false);

      // Arm the idle-nudge timer after a completed agent turn — never after a
      // suppressed one (a human owns the thread) or an errored one.
      if (!response.suppressed && !response.error && session.reengagement) {
        scheduleNudgeRef.current(session.reengagement.nudgeAfterSeconds);
      }
    } catch (err) {
      isStreamingRef.current = false;
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setIsTyping(false);
    }
  }, [ensureSession]);

  // "New conversation" — ends the current session, drops local state, and
  // restores the greeting view. Next send() will create a fresh anonymous
  // session via ensureSession(). The server-side end call is best-effort;
  // we don't block the UI on it.
  const resetSession = useCallback(() => {
    const current = sessionRef.current;
    sessionRef.current = null;
    clearNudgeTimer();
    setSessionReady(false);
    setIsTyping(false);
    setError(null);
    setActiveHandoff(null);
    handoffTextRef.current = HANDOFF_TEXT;
    isStreamingRef.current = false;
    // Restore greeting-only state. Use the resolved agentConfig.greeting so
    // we don't drop back to a generic fallback after the widget has shown
    // the customer's branded greeting.
    if (agentConfig) {
      setMessages([{ id: id(), role: 'assistant', content: agentConfig.greeting, timestamp: Date.now() }]);
    } else {
      setMessages([]);
    }
    if (current?.sessionId) {
      clientRef.current.endSession(current.sessionId).catch(() => {});
    }
    // Tell the embed loader to drop any persisted session token — the next
    // page refresh should start fresh, not resume the dead conversation.
    if (typeof window !== 'undefined' && window.parent !== window) {
      try {
        window.parent.postMessage({ type: 'chanl:session-token-cleared' }, '*');
      } catch {
        // ignore
      }
    }
  }, [agentConfig]);

  useEffect(() => {
    sendRef.current = send;
  }, [send]);

  // ── Host→iframe bridge: context, programmatic send, proactive messages ──
  //
  // The loader forwards `ChanlChat.setContext()`, `ChanlChat.sendMessage()` and
  // its queue of proactive messages across the origin boundary as
  // `chanl:set-context` / `chanl:send-message` / `chanl:init-messages`. Before
  // this listener existed only `set-user` (above) and `update-config`
  // (chat-page) had a receiver, so those three were dropped by the browser with
  // no error while embed-script.md documented them as working.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    function onHostMessage(e: MessageEvent) {
      // Same gate as `set-user`: only the embedding parent may drive the
      // widget. Origins aren't pinned (we embed on arbitrary customer
      // origins), so source-equality is the check.
      if (e.source !== window.parent) return;
      const type = (e.data as { type?: unknown } | undefined)?.type;
      if (typeof type !== 'string' || !type.startsWith('chanl:')) return;
      const payload = (e.data as { payload?: unknown }).payload;

      switch (type) {
        case 'chanl:set-context': {
          if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;
          // Merge rather than replace, so repeated calls refine the picture.
          hostContextRef.current = {
            ...(hostContextRef.current ?? {}),
            ...(payload as ClientContext),
          };
          return;
        }

        case 'chanl:send-message': {
          const text = (payload as { text?: unknown } | undefined)?.text;
          if (typeof text === 'string' && text.trim()) sendRef.current(text);
          return;
        }

        case 'chanl:init-messages': {
          // Proactive messages the loader queued while the widget was shut.
          // They render as assistant turns and are display-only — the model is
          // never told it said them.
          if (!Array.isArray(payload)) return;
          const bubbles = payload
            .filter((t): t is string => typeof t === 'string' && t.trim().length > 0)
            .map((content) => ({
              id: id(),
              role: 'assistant' as const,
              content,
              timestamp: Date.now(),
            }));
          if (bubbles.length) setMessages((p) => [...p, ...bubbles]);
          return;
        }

        // Handled by their own listeners — named so they don't trip the
        // unhandled warning below.
        case 'chanl:set-user':
        case 'chanl:update-config':
        case 'chanl:session-token-issued':
        case 'chanl:session-token-cleared':
          return;

        default:
          // A loader post with no receiver used to be pure silence. Say so in
          // development instead of letting the next one rot unnoticed.
          if (process.env.NODE_ENV !== 'production') {
            console.warn(
              `[chanl] no receiver for ${type} — the loader posted it and nothing in the widget reads it.`,
            );
          }
      }
    }

    window.addEventListener('message', onHostMessage);
    return () => window.removeEventListener('message', onHostMessage);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearNudgeTimer();
      if (sessionRef.current) {
        clientRef.current.endSession(sessionRef.current.sessionId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { messages, isTyping, error, send, sessionReady, agentConfig, resetSession, activeHandoff };
}
